
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Lead, PermissionCode, ProjectsPool, Task,
} from '@/types';
import {
  currentUserHasPermission, getLeadCustomValueBoolean, getLeadCustomValueText,
} from '@/helpers';
import API from '@/lib/API';
import SupplyHistoryIcon from '@/components/SupplyHistoryIcon.vue';

@Component({
  components: {
    SupplyHistoryIcon,
  },
})
export default class CurrentSupply extends Vue {
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;

  // Dati supply caricati dall'API come fallback
  fatherSupplyData: {
    supply1?: boolean;
    supply1text?: string;
    supply2?: boolean;
    supply2text?: string;
    supply3?: boolean;
    supply3text?: string;
    supply4?: boolean;
    supply4text?: string;
    supply5?: boolean;
    supply5text?: string;
    supplyNotes?: string;
    supply1updatedAt?: Date | null;
    supply1updatedBy?: number | null;
    supply2updatedAt?: Date | null;
    supply2updatedBy?: number | null;
    supply3updatedAt?: Date | null;
    supply3updatedBy?: number | null;
    supply4updatedAt?: Date | null;
    supply4updatedBy?: number | null;
    supply5updatedAt?: Date | null;
    supply5updatedBy?: number | null;
    supplyNotesUpdatedAt?: Date | null;
    supplyNotesUpdatedBy?: number | null;
  } | null = null;

  loading = false;

  get pool(): ProjectsPool {
    return this.$store.getters.currentPool;
  }

  get currentLead() {
    return this.$store.getters.currentLead;
  }

  get lead() {
    return this.isFather
      ? this.currentLead
      : this.father;
  }

  get father(): Lead | null {
    const firstTask: Task = this.currentLead.Tasks[0];
    const { fatherLeadId } = firstTask;

    return fatherLeadId && fatherLeadId in this.pool
      ? this.pool[fatherLeadId]
      : null;
  }

  get isChild(): boolean {
    return ['installatore-figlia', 'elettrica-figlia', 'termica-figlia'].includes(this.currentLead?.type);
  }

  get isFather(): boolean {
    return !this.isChild;
  }

  hasPermission(codename: PermissionCode): boolean { return currentUserHasPermission(codename); }

  // Metodo per caricare i dati supply dall'API
  async loadFatherSupply() {
    if (!this.currentLead || !this.isChild) return;

    this.loading = true;
    try {
      const result = await API.getLeadSupply(this.currentLead.id);
      if (!(result instanceof Error)) {
        this.fatherSupplyData = result;
      }
    } catch (error) {
      console.error('Error loading father supply:', error);
    } finally {
      this.loading = false;
    }
  }

  // Lifecycle hook per caricare i dati se necessario
  mounted() {
    // Se non abbiamo accesso al padre ma siamo su una figlia
    if (this.isChild && !this.father) {
      this.loadFatherSupply();
    }
  }

  // Watch per aggiornare i dati quando cambia la lead corrente
  @Watch('currentLead')
  onCurrentLeadChange() {
    // Ricarica i dati supply quando cambia la lead corrente
    if (this.isChild && !this.father) {
      this.loadFatherSupply();
    }
  }

  // Metodo pubblico per ricaricare i dati supply
  refreshSupply() {
    if (this.isChild && !this.father) {
      this.loadFatherSupply();
    }
  }

  get supply1() {
    if (this.lead) {
      return getLeadCustomValueBoolean(this.lead, 'supply1');
    }
    return this.fatherSupplyData?.supply1 ?? false;
  }
  get supply2() {
    if (this.lead) {
      return getLeadCustomValueBoolean(this.lead, 'supply2');
    }
    return this.fatherSupplyData?.supply2 ?? false;
  }
  get supply3() {
    if (this.lead) {
      return getLeadCustomValueBoolean(this.lead, 'supply3');
    }
    return this.fatherSupplyData?.supply3 ?? false;
  }
  get supply4() {
    if (this.lead) {
      return getLeadCustomValueBoolean(this.lead, 'supply4');
    }
    return this.fatherSupplyData?.supply4 ?? false;
  }
  get supply5() {
    if (this.lead) {
      return getLeadCustomValueBoolean(this.lead, 'supply5');
    }
    return this.fatherSupplyData?.supply5 ?? false;
  }

  get supply1text() {
    if (this.lead) {
      return getLeadCustomValueText(this.lead, 'supply1text');
    }
    return this.fatherSupplyData?.supply1text ?? '';
  }
  get supply2text() {
    if (this.lead) {
      return getLeadCustomValueText(this.lead, 'supply2text');
    }
    return this.fatherSupplyData?.supply2text ?? '';
  }
  get supply3text() {
    if (this.lead) {
      return getLeadCustomValueText(this.lead, 'supply3text');
    }
    return this.fatherSupplyData?.supply3text ?? '';
  }
  get supply4text() {
    if (this.lead) {
      return getLeadCustomValueText(this.lead, 'supply4text');
    }
    return this.fatherSupplyData?.supply4text ?? '';
  }
  get supply5text() {
    if (this.lead) {
      return getLeadCustomValueText(this.lead, 'supply5text');
    }
    return this.fatherSupplyData?.supply5text ?? '';
  }

  get supplyText() {
    if (this.supply5 && !!this.supply5text) return this.supply5text.replace('\n', '<br>');
    if (this.supply4 && !!this.supply4text) return this.supply4text.replace('\n', '<br>');
    if (this.supply3 && !!this.supply3text) return this.supply3text.replace('\n', '<br>');
    if (this.supply2 && !!this.supply2text) return this.supply2text.replace('\n', '<br>');
    if (this.supply1 && !!this.supply1text) return this.supply1text.replace('\n', '<br>');
    return null;
  }

  get supplyLabel() {
    if (this.supply5 && !!this.supply5text) return 'Fornitura definitiva';
    if (this.supply4 && !!this.supply4text) return 'Fornitura pattuita';
    if (this.supply3 && !!this.supply3text) return 'Disponibilità materiali';
    if (this.supply2 && !!this.supply2text) return 'Progettazione preliminare';
    if (this.supply1 && !!this.supply1text) return 'Fornitura contrattuale';
    return null;
  }

  get isVisible() {
    // Visibile se abbiamo accesso al lead O se abbiamo caricato i dati dall'API
    if (!this.lead && !this.fatherSupplyData) return false;
    return this.hasPermission('supply.show');
  }

  get isEditable() {
    if (this.readonly) return false;

    // Se abbiamo accesso al lead, controlla il suo status
    if (this.lead) {
      return (this.lead.status === 'open' || this.lead.status === 'new')
        && this.hasPermission('supply.edit');
    }

    // Se non abbiamo accesso al lead ma abbiamo i dati dall'API,
    // controlla lo status della currentLead (lead figlia)
    if (this.fatherSupplyData && this.currentLead) {
      return (this.currentLead.status === 'open' || this.currentLead.status === 'new')
        && this.hasPermission('supply.edit');
    }

    return false;
  }

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }
}
